/**
 * Group dance (รำกลุ่ม, 群舞). A leader opens a ring of five; party members take their places and
 * hired dancers fill the rest. For ten rounds the dancers take turns, in ring order, pressing a
 * sequence of arrows on the beat. Every arrow is graded perfect, good or miss; the ring's harmony
 * rises while everyone keeps time and falls on a miss. While the ring dances, cultivation builds
 * faster; at the end it pays training points and leaves a buff (พลังประสาน) that speeds
 * cultivation and neigong training for a while. Tuning is data/groupDance.ts; design is
 * docs/systems/GROUP_DANCE.md.
 *
 * Multiplayer: every timing is world time (World.step). An input carries the world time it was
 * pressed at, which the server clamps to at most `maxLag` old, and an arrow is only called missed
 * once that grace has passed, so a lagging press is judged as it was pressed.
 */
import { CULTIVATION, rankFor } from './cultivation';
import { EMOTES } from './data/emotes';
import { combatBlockText } from './data/combat';
import {
  DANCE_CALM,
  DANCE_DIRS,
  DANCE_END_TEXT,
  DANCE_FALLBACK,
  DANCE_LIVELY,
  DANCE_RANKS,
  DANCE_ROUNDS,
  GROUP_DANCE,
  HIRED_DANCERS,
  HIRED_TITLE,
  type DanceDir,
  type DanceEndReason,
  type DanceGrade,
} from './data/groupDance';
import { freeAt, nearestFloor } from './path';
import { mulberry32, type Rng } from './rng';
import type { PlayerProgress } from './progress';
import type { TrainKind } from './training';
import type { Entity, EntityKind, World, WorldEvent } from './world';

const G = GROUP_DANCE;

// ───────────────────────── saved state ─────────────────────────

/** What the group dance keeps in the save: today's paid sessions and the buff left. */
export interface GroupDanceState {
  /** The calendar day `runs` counts for (the arena's day, set by the client's calendar). */
  day: string;
  /** Sessions that paid today. */
  runs: number;
  /** พลังประสาน: cultivation × buffMult for buffLeft more seconds online. */
  buffMult: number;
  buffLeft: number;
  /** Sessions danced through all ten rounds, ever. */
  sessions: number;
}

export const newGroupDance = (): GroupDanceState => ({
  day: '',
  runs: 0,
  buffMult: 1,
  buffLeft: 0,
  sessions: 0,
});

/** A saved group-dance state kept within its limits (saves from before it start fresh). */
export function parseGroupDance(v: unknown): GroupDanceState {
  if (!v || typeof v !== 'object') return newGroupDance();
  const r = v as Record<string, unknown>;
  const num = (x: unknown, lo: number, hi: number, d: number) =>
    typeof x === 'number' && Number.isFinite(x) ? Math.min(hi, Math.max(lo, x)) : d;
  const buffLeft = num(r.buffLeft, 0, G.buffSeconds, 0);
  return {
    day: typeof r.day === 'string' ? r.day : '',
    runs: Math.floor(num(r.runs, 0, G.dailyRuns, 0)),
    buffMult: buffLeft > 0 ? num(r.buffMult, 1, 1 + G.buffPerf, 1) : 1,
    buffLeft,
    sessions: Math.floor(num(r.sessions, 0, Number.MAX_SAFE_INTEGER, 0)),
  };
}

// ───────────────────────── commands and events ─────────────────────────

export type DanceCommand =
  /** Open a ring here as its leader (party members within reach join, hired dancers fill the rest). */
  | { t: 'danceStart' }
  /** Take (back) your place in a ring that is still forming. */
  | { t: 'danceJoin' }
  /** Step out of the ring. */
  | { t: 'danceLeave' }
  /** An arrow pressed on your turn, at this world time (World.time when the key went down). */
  | { t: 'danceInput'; dir: DanceDir; at: number };

export interface DanceReward {
  grade: string;
  /** Group performance, 0–1. */
  perf: number;
  /** Rounds danced (10 for a whole session). */
  rounds: number;
  /** Training points (แต้มฝึกปรือ) paid. */
  cultivation: number;
  /** The buff: cultivation and neigong training × mult for `seconds`. */
  mult: number;
  seconds: number;
  /** Paying sessions left today. */
  runsLeft: number;
}

export type DanceEvent =
  /** A ring formed: its dancers in turn order (entity ids), and when the first turn begins. */
  | { type: 'danceStart'; leader: number; dancers: number[]; startsAt: number; rewarded: boolean }
  /** A dancer took their place or left it. */
  | { type: 'danceJoin'; id: number; joined: boolean }
  /** A turn began: whose, and the arrows with the world time each falls on. `skipped`: the dancer is gone. */
  | {
      type: 'danceTurn';
      round: number;
      turn: number;
      dancer: number;
      arrows: DanceDir[];
      beats: number[];
      skipped: boolean;
    }
  /** An arrow was judged. */
  | {
      type: 'danceArrow';
      dancer: number;
      index: number;
      dir: DanceDir;
      grade: DanceGrade;
      points: number;
      harmony: number;
      combo: number;
    }
  | { type: 'danceRound'; round: number; harmony: number; score: number }
  /** The ring broke up; `reward` is what the player was paid (null: nothing). */
  | { type: 'danceEnd'; reason: DanceEndReason; reward: DanceReward | null };

// ───────────────────────── rules (pure) ─────────────────────────

/** The grade of a press `delta` seconds from its beat with the right arrow (null: too early to count). */
export function judge(delta: number): DanceGrade | null {
  if (delta < -G.early) return null;
  const d = Math.abs(delta);
  return d <= G.perfect ? 'perfect' : d <= G.good ? 'good' : 'miss';
}

/** Chance an AI dancer hits an arrow: hired dancers a fixed rate, fellows by the player's power rank. */
export function aiHitChance(kind: 'fellow' | 'hired', rank: number): number {
  if (kind === 'hired') return G.hiredHit;
  const f = G.fellowHit;
  return Math.min(f.max, f.base + f.perRank * Math.max(0, rank));
}

/** An AI dancer's arrow, from one roll of the session's Rng. */
export function aiGrade(hit: number, roll: number): DanceGrade {
  return roll < hit * G.aiPerfectShare ? 'perfect' : roll < hit ? 'good' : 'miss';
}

/** The session's grade letter for a performance of 0–1. */
export const danceGradeOf = (perf: number): (typeof DANCE_RANKS)[number] =>
  DANCE_RANKS.find((r) => perf >= r.min - 1e-9) ?? DANCE_RANKS[DANCE_RANKS.length - 1]!;

export interface DanceTally {
  /** The whole ring's accuracy (0–1) and harmony (0–100). */
  groupAcc: number;
  harmony: number;
  /** The player's own accuracy (0–1). */
  ownAcc: number;
  /** Rounds finished. */
  rounds: number;
  /** Party members dancing beside the player. */
  partyMembers: number;
}

/**
 * What a session pays, before the daily cap: performance is 60% the ring's accuracy and 40% its
 * harmony; the player's own share scales from half (never pressed) to whole (every arrow perfect);
 * a ring broken off early pays for the rounds it danced.
 */
export function danceReward(t: DanceTally): Omit<DanceReward, 'runsLeft'> {
  const perf = Math.min(1, Math.max(0, 0.6 * t.groupAcc + 0.4 * (t.harmony / 100)));
  const own = 0.5 + 0.5 * Math.min(1, Math.max(0, t.ownAcc));
  const frac = Math.min(1, Math.max(0, t.rounds / G.rounds));
  const party = 1 + G.partyBonus * Math.min(G.size - 1, Math.max(0, t.partyMembers));
  return {
    grade: danceGradeOf(perf).grade,
    perf: Math.round(perf * 1000) / 1000,
    rounds: t.rounds,
    cultivation: Math.round((G.rewardBase + G.rewardPerf * perf) * own * party * frac),
    mult: Math.round((1 + G.buffPerf * perf * own) * 100) / 100,
    seconds: Math.round(G.buffSeconds * frac),
  };
}

// ───────────────────────── the session ─────────────────────────

export type DancerKind = 'player' | 'fellow' | 'hired';

export interface Dancer {
  id: number;
  kind: DancerKind;
  name: string;
  /** AI dancers: chance to hit an arrow. */
  hit: number;
  /** Their place in the ring. */
  x: number;
  y: number;
  /** Gone from the ring (their turns are skipped). */
  out: boolean;
  arrows: number;
  /** Sum of the accuracy of their arrows (perfect 1, good 0.6). */
  acc: number;
  perfect: number;
  good: number;
  miss: number;
}

export interface DanceArrow {
  dir: DanceDir;
  /** The world time it falls on. */
  at: number;
  grade: DanceGrade | null;
}

export interface DanceTurnState {
  round: number;
  /** Index into the dancers (turn order). */
  dancer: number;
  arrows: DanceArrow[];
  /** When the turn began and when the next one begins. */
  start: number;
  end: number;
  skipped: boolean;
  /** Pose of the lively dance, cycled on each hit. */
  pose: number;
}

export type DancePhase = 'gather' | 'turn' | 'pause' | 'finale';

export interface DanceSession {
  leaderId: number;
  /** Middle of the ring. */
  cx: number;
  cy: number;
  /** In turn order: the leader first, then party members, then hired dancers. */
  dancers: Dancer[];
  phase: DancePhase;
  /** World time the gathering, pause or finale ends. */
  phaseEnd: number;
  /** The round under way (0-based) and rounds finished. */
  round: number;
  roundsDone: number;
  turn: DanceTurnState | null;
  harmony: number;
  combo: number;
  maxCombo: number;
  score: number;
  /** Pays at the end (false once today's paying sessions are used up). */
  rewarded: boolean;
  /** The session's own seed and Rng: the arrows and the AI's presses follow from them alone. */
  seed: number;
  rng: Rng;
  reward: DanceReward | null;
}

/** What the dance needs from the World besides its public state (World passes its own privates). */
export interface DanceHooks {
  emit(e: WorldEvent): void;
  emote(e: Entity, id: string | null): void;
  emoji(e: Entity, id: string): void;
  face(e: Entity, at: { x: number; y: number }): void;
  spawn(o: Partial<Entity> & { kind: EntityKind; x: number; y: number }): Entity;
  /** The world's Rng (only for each session's seed). */
  random(): number;
}

type DanceWorld = Pick<World, 'time' | 'progress' | 'player' | 'entities' | 'map' | 'duel' | 'partyMembers'>;

const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);
const danceEmote = (id: string): string => (EMOTES[id] ? id : DANCE_FALLBACK);

export class GroupDance {
  session: DanceSession | null = null;

  constructor(
    private readonly w: DanceWorld,
    private readonly hooks: DanceHooks,
  ) {}

  private get pr(): PlayerProgress {
    return this.w.progress;
  }

  // ── queries the World and the client use ──

  /** The player's place in the ring, or null when not dancing. */
  me(): Dancer | null {
    const d = this.session?.dancers.find((x) => x.id === this.w.player.id);
    return d && !d.out ? d : null;
  }

  /** An AI dancer standing in the ring (the World leaves them be while they dance). */
  holds(e: Entity): boolean {
    return !!this.session?.dancers.some((d) => d.id === e.id && d.kind !== 'player' && !d.out);
  }

  /** The buff's multiplier now (1 when there is none). */
  buffMult(): number {
    const g = this.pr.groupDance;
    return g.buffLeft > 0 ? g.buffMult : 1;
  }

  /**
   * Cultivation per second online, from the rate the player would have (`base`): dancing in a
   * paying ring, the meditation rate lifted by harmony; otherwise `base` with the buff.
   */
  cultivationRate(base: number): number {
    const s = this.session;
    if (s && s.phase !== 'finale' && this.me())
      return s.rewarded ? CULTIVATION.meditateRate * (1 + (s.harmony / 100) * G.duringBonus) : base;
    return base * this.buffMult();
  }

  /** How much faster training runs: neigong (การฝึกปราณ) with the buff. */
  trainMult(kind: TrainKind): number {
    return kind === 'neigong' ? this.buffMult() : 1;
  }

  /** Paying sessions left today. */
  runsLeft(): number {
    this.rollDay();
    return Math.max(0, G.dailyRuns - this.pr.groupDance.runs);
  }

  // ── commands ──

  command(c: DanceCommand): void {
    switch (c.t) {
      case 'danceStart':
        return this.start();
      case 'danceJoin':
        return this.join();
      case 'danceLeave': {
        const d = this.me();
        if (d) this.out(d, 'left');
        return;
      }
      case 'danceInput':
        return this.input(c.dir, c.at);
    }
  }

  private notice(text: string): void {
    this.hooks.emit({ type: 'notice', text });
  }

  /** A new calendar day: today's paying sessions start over. */
  private rollDay(): void {
    const g = this.pr.groupDance;
    const day = this.pr.arena.day;
    if (g.day !== day) {
      g.day = day;
      g.runs = 0;
    }
  }

  private start(): void {
    const w = this.w;
    const p = w.player;
    if (this.session) return this.notice('กำลังรำกลุ่มอยู่');
    if (p.dead || p.jailT > 0) return;
    if (w.duel) return this.notice('อยู่ระหว่างประลอง รำกลุ่มไม่ได้');
    if (p.combatT > 0) return this.notice(combatBlockText(p, 'รำกลุ่มไม่ได้'));
    if (p.mount) return this.notice('ลงจากพาหนะก่อนจึงรำกลุ่มได้');
    if (p.moving || p.z > 0.5) return this.notice('ต้องยืนนิ่งก่อนจึงรำกลุ่มได้');
    const rewarded = this.runsLeft() > 0;
    const seed = Math.floor(this.hooks.random() * 2 ** 32);

    // The leader keeps their spot at the front of the ring; the others stand round the circle.
    const a0 = Math.PI / 4;
    const R = G.ringRadius;
    const cx = p.x - Math.cos(a0) * R;
    const cy = p.y - Math.sin(a0) * R;
    const place = (k: number) => {
      const a = a0 + (2 * Math.PI * k) / G.size;
      const x = cx + Math.cos(a) * R;
      const y = cy + Math.sin(a) * R;
      if (freeAt(w.map, x, y)) return { x, y };
      const f = nearestFloor(w.map, x, y, 2);
      return f ? { x: f[0], y: f[1] } : { x: p.x, y: p.y };
    };
    const dancer = (e: Entity, kind: DancerKind, hit: number, at: { x: number; y: number }): Dancer => ({
      id: e.id,
      kind,
      name: e.name,
      hit,
      x: at.x,
      y: at.y,
      out: false,
      arrows: 0,
      acc: 0,
      perfect: 0,
      good: 0,
      miss: 0,
    });
    const dancers: Dancer[] = [dancer(p, 'player', 0, { x: p.x, y: p.y })];
    const rank = rankFor(this.pr.cultivationSpent).index;
    for (const e of w.partyMembers()) {
      if (dancers.length >= G.size) break;
      if (e.dead || dist(e, p) > G.joinRange) continue;
      const at = place(dancers.length);
      Object.assign(e, { x: at.x, y: at.y, path: null, targetId: null, moving: false });
      dancers.push(dancer(e, 'fellow', aiHitChance('fellow', rank), at));
    }
    for (let k = 0; dancers.length < G.size; k++) {
      const def = HIRED_DANCERS[k % HIRED_DANCERS.length]!;
      const at = place(dancers.length);
      const e = this.hooks.spawn({
        kind: 'ambient',
        x: at.x,
        y: at.y,
        name: def.name,
        title: HIRED_TITLE,
        sprite: def.sprite,
      });
      dancers.push(dancer(e, 'hired', aiHitChance('hired', 0), at));
    }
    p.path = null;
    const s: DanceSession = {
      leaderId: p.id,
      cx,
      cy,
      dancers,
      phase: 'gather',
      phaseEnd: w.time + G.gatherSeconds,
      round: 0,
      roundsDone: 0,
      turn: null,
      harmony: G.harmonyStart,
      combo: 0,
      maxCombo: 0,
      score: 0,
      rewarded,
      seed,
      rng: mulberry32(seed),
      reward: null,
    };
    this.session = s;
    for (const d of dancers) {
      const e = w.entities.get(d.id)!;
      this.hooks.face(e, { x: cx, y: cy });
      this.hooks.emote(
        e,
        danceEmote(DANCE_CALM[d.kind === 'player' ? 0 : dancers.indexOf(d) % DANCE_CALM.length]!),
      );
    }
    this.hooks.emit({
      type: 'danceStart',
      leader: p.id,
      dancers: dancers.map((d) => d.id),
      startsAt: s.phaseEnd,
      rewarded,
    });
    this.hooks.emit({
      type: 'log',
      channel: 'sys',
      text: rewarded
        ? `ตั้งวงรำกลุ่ม (${G.size} คน ${G.rounds} รอบ) · รับรางวัลได้อีก ${this.runsLeft()} ครั้งวันนี้`
        : `วันนี้รับรางวัลรำกลุ่มครบ ${G.dailyRuns} ครั้งแล้ว รำต่อเพื่อความสนุกได้`,
    });
  }

  private join(): void {
    const s = this.session;
    const p = this.w.player;
    const d = s?.dancers.find((x) => x.id === p.id);
    if (!s || !d) return this.notice('ไม่มีวงรำให้เข้าร่วม');
    if (!d.out) return;
    if (s.phase !== 'gather') return this.notice('วงรำเริ่มไปแล้ว');
    if (dist(p, d) > G.joinRange || p.dead || p.combatT > 0 || p.mount)
      return this.notice('เข้าใกล้วงรำก่อน');
    Object.assign(p, { x: d.x, y: d.y, path: null });
    d.out = false;
    this.hooks.face(p, { x: s.cx, y: s.cy });
    this.hooks.emote(p, danceEmote(DANCE_CALM[0]!));
    this.hooks.emit({ type: 'danceJoin', id: p.id, joined: true });
  }

  private input(dir: DanceDir, at: number): void {
    const s = this.session;
    const t = s?.turn;
    const me = this.me();
    if (!s || !t || s.phase !== 'turn' || !me || s.dancers[t.dancer] !== me) return;
    if (!DANCE_DIRS.includes(dir)) return;
    const now = this.w.time;
    // The press is judged when it was made, but never later than now nor older than the lag allowed.
    const when = Number.isFinite(at) ? Math.min(now, Math.max(now - G.maxLag, at)) : now;
    const k = t.arrows.findIndex((a) => a.grade === null);
    if (k < 0) return;
    const a = t.arrows[k]!;
    const g = judge(when - a.at);
    if (g === null) return;
    this.resolve(s, t, k, dir === a.dir ? g : 'miss');
  }

  // ── the clock ──

  step(dt: number): void {
    const g = this.pr.groupDance;
    if (g.buffLeft > 0) {
      g.buffLeft = Math.max(0, g.buffLeft - dt);
      if (g.buffLeft <= 0) {
        g.buffMult = 1;
        this.notice('พลังประสานจากการรำกลุ่มหมดแล้ว');
      }
    }
    const s = this.session;
    if (!s) return;
    if (s.phase !== 'finale') this.watch(s);
    if (this.session !== s) return;
    const now = this.w.time;
    switch (s.phase) {
      case 'gather':
        if (now < s.phaseEnd) return;
        if (!this.humans(s)) return this.end(s, 'alone');
        return this.beginTurn(s, 0, 0);
      case 'pause':
        if (now >= s.phaseEnd) this.beginTurn(s, s.round + 1, 0);
        return;
      case 'finale':
        if (now >= s.phaseEnd) this.breakUp(s);
        return;
      case 'turn': {
        const t = s.turn!;
        const d = s.dancers[t.dancer]!;
        t.arrows.forEach((a, k) => {
          if (a.grade !== null) return;
          if (d.kind !== 'player' && now >= a.at) this.resolve(s, t, k, aiGrade(d.hit, s.rng()));
          else if (now > a.at + G.good + G.maxLag) this.resolve(s, t, k, 'miss');
        });
        if (now >= t.end && t.arrows.every((a) => a.grade !== null)) this.endTurn(s, t);
        return;
      }
    }
  }

  private humans(s: DanceSession): boolean {
    return s.dancers.some((d) => d.kind === 'player' && !d.out);
  }

  /** Interruptions: the player struck, fallen or wandered off; an AI dancer gone. */
  private watch(s: DanceSession): void {
    for (const d of s.dancers) {
      if (d.out) continue;
      const e = this.w.entities.get(d.id);
      if (d.kind === 'player') {
        if (!e || e.dead) this.out(d, 'fallen');
        else if (e.combatT > 0 || e.hitT > 0) this.out(d, 'attacked');
        else if (dist(e, d) > G.leaveDistance || e.mount || e.meditating) this.out(d, 'left');
      } else if (!e || e.dead || e.combatT > 0 || dist(e, d) > G.leaveDistance + 1) this.out(d, 'left');
      if (this.session !== s) return;
    }
  }

  /** A dancer leaves the ring. Without a player left in it (once dancing), the ring breaks up. */
  private out(d: Dancer, why: DanceEndReason): void {
    const s = this.session!;
    d.out = true;
    const e = this.w.entities.get(d.id);
    if (e && e.emote) this.hooks.emote(e, null);
    this.hooks.emit({ type: 'danceJoin', id: d.id, joined: false });
    const t = s.turn;
    if (t && s.phase === 'turn' && s.dancers[t.dancer] === d) {
      t.skipped = true;
      t.arrows.forEach((a, k) => a.grade === null && this.resolve(s, t, k, 'miss', true));
    }
    if (d.kind !== 'player') {
      if (e) this.hooks.emit({ type: 'log', channel: 'sys', text: `${d.name}หลุดจากวงรำ วงรำต่อไป` });
      return;
    }
    if (s.phase !== 'gather' && !this.humans(s)) this.end(s, why);
  }

  private beginTurn(s: DanceSession, round: number, k: number): void {
    const now = this.w.time;
    const def = DANCE_ROUNDS[round]!;
    const d = s.dancers[k]!;
    // The arrows are drawn whether or not the dancer is still here, so the sequence never shifts.
    const arrows: DanceArrow[] = Array.from({ length: def.arrows }, (_, i) => ({
      dir: DANCE_DIRS[Math.floor(s.rng() * DANCE_DIRS.length)]!,
      at: now + G.leadIn + i * def.beat,
      grade: null,
    }));
    const last = arrows[arrows.length - 1]!.at;
    const t: DanceTurnState = {
      round,
      dancer: k,
      arrows,
      start: now,
      end: d.out ? now + G.turnGap : last + G.good + G.maxLag + G.turnGap,
      skipped: d.out,
      pose: round % DANCE_LIVELY.length,
    };
    s.phase = 'turn';
    s.round = round;
    s.turn = t;
    this.hooks.emit({
      type: 'danceTurn',
      round,
      turn: k,
      dancer: d.id,
      arrows: arrows.map((a) => a.dir),
      beats: arrows.map((a) => a.at),
      skipped: d.out,
    });
    if (d.out) {
      arrows.forEach((_, i) => this.resolve(s, t, i, 'miss', true));
      return;
    }
    // The dancer whose turn it is dances the lively one, a note over their head; the rest sway.
    s.dancers.forEach((o, i) => {
      const e = this.w.entities.get(o.id);
      if (!e || o.out) return;
      this.hooks.emote(e, danceEmote(i === k ? DANCE_LIVELY[t.pose]! : DANCE_CALM[i % DANCE_CALM.length]!));
      if (i === k) this.hooks.emoji(e, 'note');
    });
  }

  private resolve(s: DanceSession, t: DanceTurnState, k: number, grade: DanceGrade, skipped = false): void {
    const a = t.arrows[k]!;
    a.grade = grade;
    const d = s.dancers[t.dancer]!;
    d.arrows++;
    d.acc += G.accuracy[grade];
    d[grade]++;
    let points = 0;
    // A skipped turn counts its arrows as missed but leaves harmony and the combo alone.
    if (!skipped) {
      if (grade === 'miss') s.combo = 0;
      else {
        points = Math.round(G.points[grade] * (1 + Math.min(s.combo, G.comboCap) / 100));
        s.combo++;
        s.maxCombo = Math.max(s.maxCombo, s.combo);
      }
      s.score += points;
      s.harmony = Math.min(100, Math.max(0, s.harmony + G.harmony[grade]));
      const e = this.w.entities.get(d.id);
      // A hit changes the lively dancer's pose.
      if (e && grade !== 'miss') {
        t.pose = (t.pose + 1) % DANCE_LIVELY.length;
        this.hooks.emote(e, danceEmote(DANCE_LIVELY[t.pose]!));
      }
    }
    this.hooks.emit({
      type: 'danceArrow',
      dancer: d.id,
      index: k,
      dir: a.dir,
      grade,
      points,
      harmony: s.harmony,
      combo: s.combo,
    });
  }

  private endTurn(s: DanceSession, t: DanceTurnState): void {
    if (!t.skipped && t.arrows.every((a) => a.grade !== 'miss'))
      s.harmony = Math.min(100, s.harmony + G.cleanTurn);
    if (t.dancer < s.dancers.length - 1) return this.beginTurn(s, t.round, t.dancer + 1);
    s.roundsDone = t.round + 1;
    this.hooks.emit({ type: 'danceRound', round: t.round, harmony: s.harmony, score: s.score });
    if (s.roundsDone >= G.rounds) return this.end(s, 'done');
    s.phase = 'pause';
    s.turn = null;
    s.phaseEnd = this.w.time + G.roundPause;
  }

  /** The tally the reward is figured from. */
  tally(s: DanceSession): DanceTally {
    let arrows = 0;
    let acc = 0;
    for (const d of s.dancers) {
      arrows += d.arrows;
      acc += d.acc;
    }
    const me = s.dancers.find((d) => d.id === this.w.player.id)!;
    return {
      groupAcc: arrows ? acc / arrows : 0,
      harmony: s.harmony,
      ownAcc: me.arrows ? me.acc / me.arrows : 0,
      rounds: s.roundsDone,
      partyMembers: s.dancers.filter((d) => d.kind === 'fellow' && !d.out).length,
    };
  }

  private end(s: DanceSession, reason: DanceEndReason): void {
    const pr = this.pr;
    const g = pr.groupDance;
    let reward: DanceReward | null = null;
    if (s.rewarded && (reason === 'done' || s.roundsDone >= G.partialMinRounds)) {
      this.rollDay();
      const r = danceReward(this.tally(s));
      g.runs++;
      pr.cultivation += r.cultivation;
      // The stronger buff wins; the longer time is kept.
      if (g.buffLeft <= 0 || r.mult >= g.buffMult) g.buffMult = r.mult;
      g.buffLeft = Math.max(g.buffLeft, r.seconds);
      reward = { ...r, runsLeft: Math.max(0, G.dailyRuns - g.runs) };
    }
    if (reason === 'done') g.sessions++;
    s.reward = reward;
    s.turn = null;
    this.hooks.emit({ type: 'danceEnd', reason, reward });
    this.hooks.emit({
      type: 'log',
      channel: 'sys',
      text: reward
        ? `${DANCE_END_TEXT[reason]} · ระดับ ${reward.grade} · ได้แต้มฝึกปรือ ${reward.cultivation} · พลังประสาน ×${reward.mult} ${Math.round(reward.seconds / 60)} นาที`
        : DANCE_END_TEXT[reason],
    });
    if (reward) this.hooks.emit({ type: 'progressChanged' });
    if (reason !== 'done') return this.breakUp(s);
    // The ring dances on a moment in celebration, then breaks up.
    s.phase = 'finale';
    s.phaseEnd = this.w.time + G.finaleSeconds;
    for (const d of s.dancers) {
      const e = this.w.entities.get(d.id);
      if (e && !d.out) this.hooks.emote(e, danceEmote(DANCE_LIVELY[0]!));
    }
  }

  /** The ring parts: dancers stop, hired dancers go home. */
  private breakUp(s: DanceSession): void {
    for (const d of s.dancers) {
      const e = this.w.entities.get(d.id);
      if (!e) continue;
      if (d.kind === 'hired') this.w.entities.delete(d.id);
      else if (!d.out && e.emote) this.hooks.emote(e, null);
    }
    this.session = null;
  }
}
